package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AdminPanelSettings
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.BuiltInRole
import com.evoapps.evoshift.domain.model.CustomRole
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.ui.adaptive.WideContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.viewmodels.RoleDraft
import com.evoapps.evoshift.viewmodels.RolesEvent
import com.evoapps.evoshift.viewmodels.RolesViewModel
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.SurfaceCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.edit
import evoshift.composeapp.generated.resources.roles_builtin
import evoshift.composeapp.generated.resources.roles_copy
import evoshift.composeapp.generated.resources.roles_custom
import evoshift.composeapp.generated.resources.roles_delete_confirm
import evoshift.composeapp.generated.resources.roles_edit_title
import evoshift.composeapp.generated.resources.roles_intro
import evoshift.composeapp.generated.resources.roles_name
import evoshift.composeapp.generated.resources.roles_name_error
import evoshift.composeapp.generated.resources.roles_new_title
import evoshift.composeapp.generated.resources.roles_none
import evoshift.composeapp.generated.resources.roles_profile_intro
import evoshift.composeapp.generated.resources.roles_summary
import evoshift.composeapp.generated.resources.roles_title
import evoshift.composeapp.generated.resources.save
import org.jetbrains.compose.resources.stringResource

/*
 * SH-270: "Role i uprawnienia", from the owner's profile. The built-in roles (fixed: they give
 * what the roles always gave) with a copy button, the business's own roles with edit and delete.
 * The editor is the features x levels table; a saved change reaches everyone with the role.
 */

@Composable
fun RolesScreen(
    viewModel: RolesViewModel,
    onNavigateBack: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    var toDelete by remember { mutableStateOf<CustomRole?>(null) }
    val errorText = state.error?.asString()
    val successText = state.successMessage?.asString()
    LaunchedEffect(errorText, successText) {
        (errorText ?: successText)?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.onEvent(RolesEvent.MessageShown)
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.roles_title), onBack = onNavigateBack) }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().contentWidth(WideContentMaxWidth)) {
            val draft = state.draft
            Column(
                Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (draft != null) {
                    RoleEditor(
                        draft = draft,
                        saving = state.isSaving,
                        onRename = { viewModel.onEvent(RolesEvent.Rename(it)) },
                        onLevel = { f, l -> viewModel.onEvent(RolesEvent.SetLevel(f, l)) },
                        onSave = { viewModel.onEvent(RolesEvent.Save) },
                        onCancel = { viewModel.onEvent(RolesEvent.CloseEditor) }
                    )
                } else {
                    Text(
                        stringResource(Res.string.roles_intro),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(stringResource(Res.string.roles_builtin), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    state.builtIn.forEach { role ->
                        RoleCard(
                            name = role.permission.label(),
                            levels = role.levels,
                            actions = {
                                if (role != BuiltInRole.OWNER) {
                                    IconButton(onClick = { viewModel.onEvent(RolesEvent.New(role)) }, enabled = !state.isSaving) {
                                        Icon(Icons.Default.ContentCopy, contentDescription = stringResource(Res.string.roles_copy, role.permission.label()))
                                    }
                                }
                            }
                        )
                    }
                    Text(stringResource(Res.string.roles_custom), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    if (state.roles.isEmpty() && !state.isLoading) {
                        Text(stringResource(Res.string.roles_none), style = MaterialTheme.typography.bodySmall)
                    }
                    state.roles.forEach { role ->
                        RoleCard(
                            name = role.name,
                            levels = role.levels,
                            actions = {
                                IconButton(onClick = { viewModel.onEvent(RolesEvent.Edit(role.id)) }, enabled = !state.isSaving) {
                                    Icon(Icons.Default.Edit, contentDescription = stringResource(Res.string.edit))
                                }
                                IconButton(onClick = { toDelete = role }, enabled = !state.isSaving) {
                                    Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.delete))
                                }
                            }
                        )
                    }
                }
                Spacer(Modifier.height(48.dp))
            }
            LoadingOverlay(isLoading = state.isLoading)
        }
    }

    toDelete?.let { role ->
        EvoConfirmDialog(
            title = stringResource(Res.string.delete),
            text = stringResource(Res.string.roles_delete_confirm, role.name),
            confirmLabel = stringResource(Res.string.delete),
            onConfirm = {
                viewModel.onEvent(RolesEvent.Delete(role.id))
                toDelete = null
            },
            onDismiss = { toDelete = null },
            destructive = true,
            icon = Icons.Default.Delete
        )
    }
}

/** A role with how many features it lets edit, view or not use. */
@Composable
private fun RoleCard(name: String, levels: Map<Feature, AccessLevel>, actions: @Composable () -> Unit) {
    SurfaceCard(Modifier.fillMaxWidth()) {
        Row(Modifier.padding(start = 16.dp, top = 8.dp, bottom = 8.dp, end = 4.dp), verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.weight(1f)) {
                Text(name, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                val count = { level: AccessLevel -> Feature.entries.count { (levels[it] ?: AccessLevel.NONE) == level } }
                Text(
                    stringResource(Res.string.roles_summary, count(AccessLevel.EDIT), count(AccessLevel.VIEW), count(AccessLevel.NONE)),
                    style = MaterialTheme.typography.bodySmall
                )
            }
            actions()
        }
    }
}

@Composable
private fun RoleEditor(
    draft: RoleDraft,
    saving: Boolean,
    onRename: (String) -> Unit,
    onLevel: (Feature, AccessLevel) -> Unit,
    onSave: () -> Unit,
    onCancel: () -> Unit
) {
    Text(
        stringResource(if (draft.id == null) Res.string.roles_new_title else Res.string.roles_edit_title),
        style = MaterialTheme.typography.titleLarge,
        fontWeight = FontWeight.Bold
    )
    EvoTextField(
        value = draft.name,
        onValueChange = onRename,
        label = stringResource(Res.string.roles_name),
        error = if (draft.name.isNotEmpty() && !draft.nameValid) stringResource(Res.string.roles_name_error) else null,
        onLight = true
    )
    FeatureLevelsTable(levels = draft.levels, enabled = !saving, onLevel = onLevel)
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
        TextButton(onClick = onCancel, enabled = !saving) { Text(stringResource(Res.string.cancel)) }
        Button(onClick = onSave, enabled = !saving && draft.nameValid) { Text(stringResource(Res.string.save)) }
    }
}

/** SH-270: the profile's entry to the roles (the owner's). */
@Composable
fun RolesProfileSection(onOpen: () -> Unit) {
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(stringResource(Res.string.roles_title), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
        Text(
            stringResource(Res.string.roles_profile_intro),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        TextButton(onClick = onOpen, modifier = Modifier.align(Alignment.End)) {
            Icon(Icons.Default.AdminPanelSettings, contentDescription = null)
            Spacer(Modifier.width(4.dp))
            Text(stringResource(Res.string.roles_title))
        }
    }
}
